import { makeStyles } from "tss-react/mui";
import { MiButton, MiDetailFields } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@miplatform/constants";
import { IDetailField } from "@miplatform/types";
import { mdiMinus } from "@mdi/js";
import { Box, ButtonProps, Typography } from "@mui/material";
import { DraftWindow } from "./useEditTimeWindowsModalViewModel";

export const WorkTemplateBaseAutoAssignTimeWindows = ({
  templateId,
  templateName,
  windows,
  overlappingIndices,
  isSaving,
  getWindowFields,
  onRemoveWindow,
}: WorkTemplateBaseAutoAssignTimeWindowsProps) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.windowsList}>
      {windows.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          className={classes.emptyState}
        >
          {`No ${templateName} Time Windows`}
        </Typography>
      ) : (
        windows.map((window, index) => (
          <Box key={window.id}>
            <Box className={classes.windowContainer}>
              <MiDetailFields
                containerStyle={classes.timeInputFields}
                detailFields={getWindowFields(index)}
              />
              <MiButton
                className={classes.removeButton}
                size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
                color={BUTTON_COLORS.ERROR}
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={{ path: mdiMinus }}
                onClick={() => onRemoveWindow(index)}
                disabled={isSaving}
                aria-label="Remove time window"
                description="Remove time window"
                data-testid={`remove-time-window-${templateId}-${index}`}
              />
            </Box>
            {overlappingIndices.has(index) && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ ml: 3, mb: 1, display: "block" }}
              >
                Overlapping assignment window
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};

interface WorkTemplateBaseAutoAssignTimeWindowsProps {
  templateId: string;
  templateName: string;
  windows: DraftWindow[];
  overlappingIndices: Set<number>;
  isSaving: boolean;
  getWindowFields: (index: number) => IDetailField[];
  onRemoveWindow: (index: number) => void;
}

const useStyles = makeStyles()((theme) => ({
  windowsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  windowContainer: {
    padding: theme.spacing(0, 1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  timeInputFields: {
    width: "100%",
  },
  removeButton: {
    width: 16,
    height: 16,
    minWidth: 16,
    padding: theme.spacing(2.5),
    borderRadius: "50%",
  },
  emptyState: {
    display: "block",
    textAlign: "center",
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2),
  },
}));
